import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, PackageCheck, Search, Truck, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { PoLinePicker, LineProgress, fmtQ, overMsg } from '@/components/po/PoLinePicker';
import { DeliveryPoSummary } from '@/components/po/DeliveryPoSummary';

const DeliveryCard = ({ d, selected, onPick }) => (
  <button type="button" onClick={() => onPick(d)} data-testid={`pr-delivery-${d.entry_id}`}
    className={`w-full text-left rounded-lg border p-3 transition-colors duration-150 ${selected ? 'border-[#2D5F4F] bg-emerald-50 ring-1 ring-[#2D5F4F]' : 'border-[#E5DFD0] bg-white hover:border-[#2D5F4F]/50'}`}>
    <div className="flex items-center justify-between gap-2">
      <span className="font-mono font-semibold text-sm text-[#1A1F2E]">{d.document_barcode || d.entry_id}</span>
      <span className="text-xs text-gray-500">{d.received_count}/{d.bundle_count} bundles</span>
    </div>
    <p className="text-sm text-gray-700 truncate">{d.vendor_name}</p>
    <div className="flex flex-wrap items-center gap-1.5 mt-1 text-xs text-gray-500">
      {d.vendor_doc_no && <span>Doc {d.vendor_doc_no}</span>}
      {d.vendor_doc_date && <span>· {d.vendor_doc_date}</span>}
      {d.po_numbers.map((n) => <Badge key={n} variant="outline" className="font-mono text-[10px] border-blue-300 text-blue-700">{n}</Badge>)}
      {d.counted_qty > 0 && <span className="text-emerald-700 font-medium">· {fmtQ(d.counted_qty)} counted</span>}
    </div>
  </button>
);

const LineEntry = ({ line, value, remark, onQty, onRemark }) => (
  <div className="rounded-lg border border-[#E5DFD0] bg-white p-3" data-testid={`pr-line-${line.po_item_id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-medium text-[#1A1F2E] truncate">{line.supplier_design_name}</p>
        {line.mapped_sort_no && <p className="text-xs font-mono text-purple-700">{line.mapped_sort_no}</p>}
      </div>
      <div className="text-right shrink-0">
        <p className="text-[11px] text-gray-500">Balance</p>
        <p className="text-lg font-bold text-amber-700 tabular-nums" data-testid={`pr-balance-${line.po_item_id}`}>{fmtQ(line.balance_qty)} <span className="text-xs font-normal">{line.unit}</span></p>
      </div>
    </div>
    <LineProgress line={line} pending={Number(value) || 0} testId={`pr-progress-${line.po_item_id}`} />
    <div className="flex gap-2 mt-2">
      <Input type="number" inputMode="decimal" min="0" step="any" value={value} onChange={(e) => onQty(e.target.value)}
        placeholder={`Qty (${line.unit})`} className="h-12 text-lg font-semibold w-36 shrink-0 placeholder:text-sm placeholder:font-normal" data-testid={`pr-qty-${line.po_item_id}`} />
      <Input value={remark} onChange={(e) => onRemark(e.target.value)} placeholder="Remarks" className="h-12" data-testid={`pr-remarks-${line.po_item_id}`} />
    </div>
  </div>
);

/** Other sections: record the quantity received per PO line, against a delivery already received in this section. */
export default function PurchaseReceipt() {
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const [params] = useSearchParams();
  const section = params.get('section') || sectionId;
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');
  const [delivery, setDelivery] = useState(null);
  const [poSel, setPoSel] = useState({ po: null, line: null, pct: 5 });
  const [qty, setQty] = useState({});
  const [remarks, setRemarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(() => {
    setList(null);
    apiClient.get('/po-receipts/deliveries', { params: { section, q } }).then((r) => setList(r.data.deliveries || []))
      .catch((e) => { setList([]); toast.error(e.response?.data?.detail || 'Failed to load deliveries'); });
  }, [section, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, refresh]);

  const pick = (d) => { setDelivery(d); setPoSel({ po: null, line: null, pct: 5 }); setQty({}); setRemarks({}); };
  const lines = useMemo(() => poSel.po?.lines || [], [poSel.po]);

  const save = async () => {
    const rows = lines.filter((l) => Number(qty[l.po_item_id]) > 0);
    if (!rows.length) return toast.error('Type the quantity received for at least one line');
    const over = rows.map((l) => overMsg(poSel.po, l, poSel.pct, Number(qty[l.po_item_id]), 'this receipt')).find(Boolean);
    if (over) return toast.error(over, { duration: 9000 });
    setSaving(true);
    try {
      const r = await apiClient.post('/po-receipts/section', {
        section, entry_id: delivery.entry_id, po_id: poSel.po.po_id,
        lines: rows.map((l) => ({ po_item_id: l.po_item_id, qty: Number(qty[l.po_item_id]), remarks: remarks[l.po_item_id] || '' })),
      });
      toast.success(`Saved ${r.data.count} line(s) on ${poSel.po.po_number} · PO is now ${r.data.po_status.replace('_', ' ').toLowerCase()}`);
      setQty({}); setRemarks({}); setRefresh((k) => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the receipt', { duration: 9000 });
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="purchase-receipt-page">
      <header className="bg-[#1A1F2E] text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-4 min-h-16 py-2 md:py-0 md:h-16 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/sections/${sectionId}`)} className="text-white hover:bg-white/10" data-testid="pr-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex-1 min-w-0 pt-6 md:pt-0">
            <h1 className="text-lg md:text-xl font-bold flex items-center gap-2"><PackageCheck className="w-5 h-5 text-emerald-300" />Purchase Receipt</h1>
            <p className="text-xs md:text-sm text-gray-400 truncate">{section} · goods received against a Purchase Order</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setRefresh((k) => k + 1)} className="text-white hover:bg-white/10" data-testid="pr-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-6 grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-[#1A1F2E] flex items-center gap-2"><Truck className="w-4 h-4" />1. Pick the delivery</h2>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search document, supplier, invoice no" className="pl-9 h-11" data-testid="pr-delivery-search" />
          </div>
          {list === null ? <Loader2 className="w-6 h-6 animate-spin text-[#2D5F4F] mx-auto" />
            : list.length === 0 ? <p className="text-sm text-gray-500 py-6 text-center" data-testid="pr-no-deliveries">No supplier deliveries received in {section} yet — receive the bundles in Bundle Receipt Entry first.</p>
              : <div className="grid gap-2 max-h-[65vh] overflow-y-auto pr-1">{list.map((d) => <DeliveryCard key={d.entry_id} d={d} selected={delivery?.entry_id === d.entry_id} onPick={pick} />)}</div>}
        </section>
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-[#1A1F2E]">2. Pick the PO and type the quantity received</h2>
          {!delivery ? <p className="text-sm text-gray-500 rounded-lg border border-dashed border-[#E5DFD0] p-6 text-center">Pick a delivery on the left first.</p> : (
            <>
              <DeliveryPoSummary entryId={delivery.entry_id} refreshKey={refresh} />
              <PoLinePicker section={section} vendorName={delivery.vendor_name} poId={poSel.po?.po_id} lineId={null} allowNone={false} showLines={false}
                onChange={(s) => setPoSel(s)} refreshKey={refresh} testPrefix="pr-po" />
              {poSel.po && (
                <div className="space-y-2">
                  {lines.map((l) => <LineEntry key={l.po_item_id} line={l} value={qty[l.po_item_id] || ''} remark={remarks[l.po_item_id] || ''}
                    onQty={(v) => setQty((p) => ({ ...p, [l.po_item_id]: v }))} onRemark={(v) => setRemarks((p) => ({ ...p, [l.po_item_id]: v }))} />)}
                  <Button onClick={save} disabled={saving} className="w-full h-12 bg-[#2D5F4F] hover:bg-[#234A3D] text-base" data-testid="pr-save-btn">
                    {saving ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <PackageCheck className="w-5 h-5 mr-2" />}Save receipt on {poSel.po.po_number}
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
}
